import { Box, Flex } from "@chakra-ui/react";
import { QueryHookOptions, useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback, useMemo } from "react";
import {
  GroupBase,
  OptionProps,
  SelectInstance,
  components,
} from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { DistributionCenterFilter } from "__generated__/gql/graphql";
import useLatestRef from "utils/useLatestRef";
import DistributionCenterName, {
  BaseDistributionCenterName,
} from "components/Shared/OrderingPlatformDirectory/DistributionCenters/DistributionCenterName";
import { isNewOption } from "components/Shared/Select/utils";
import { useCurrentBrandId } from "utils/currentUserHooks";
import ArchivedBadge from "components/Shared/ArchivedBadge";

const valueOptionFragment = gql(/* GraphQL */ `
  fragment DistributionCenterSelectOption on DistributionCenter {
    id
    name
    fullDisplayName
    isArchived
  }
`);

const menuOptionFragment = gql(/* GraphQL */ `
  fragment DistributionCenterSelectMenuOption on DistributionCenter {
    id
    name
    code
    isArchived
    orderingPlatform {
      id
      name
    }
    address {
      id
      formatted
    }
    ...DistributionCenterBaseNameFragment
    ...DistributionCenterNameFragment @skip(if: $isOrderingPlatformHidden)
  }
`);

const optionFragment = gql(/* GraphQL */ `
  fragment DistributionCenterSelectValueOrMenuOption on DistributionCenter {
    id
    ...DistributionCenterSelectOption
    ...DistributionCenterSelectMenuOption @include(if: $isMenuOption)
  }
`);

const valueQuery = gql(/* GraphQL */ `
  query DistributionCenterSelectValue(
    $ids: [UUID!]!
    $isMenuOption: Boolean = false
    $isOrderingPlatformHidden: Boolean = false
  ) {
    distributionCentersList(filter: { id: { in: $ids } }) {
      ...DistributionCenterSelectValueOrMenuOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query DistributionCenterSelectOptions(
    $filter: DistributionCenterFilter
    $limit: Int = 10
    $isMenuOption: Boolean = true
    $isOrderingPlatformHidden: Boolean = false
  ) {
    distributionCenters(
      filter: $filter
      orderBy: FULL_DISPLAY_NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...DistributionCenterSelectValueOrMenuOption
      }
    }
  }
`);

export type Option = DocumentType<typeof optionFragment>;

function makeDistributionCenterOption(isOrderingPlatformHidden = false) {
  function DistributionCenterOption(props: OptionProps<Option>) {
    const { data } = props;
    if (isNewOption(data)) {
      return <components.Option {...props}>{data.label}</components.Option>;
    }

    const menuOption = getFragmentData(menuOptionFragment, data);
    return (
      <components.Option {...props}>
        <Flex
          gap={2}
          justifyContent="space-between"
          data-is-archived={menuOption.isArchived || undefined}
          sx={{ "&[data-is-archived=true]": { opacity: 0.65 } }}
        >
          {isOrderingPlatformHidden ? (
            <BaseDistributionCenterName distributionCenter={menuOption} />
          ) : (
            <DistributionCenterName
              distributionCenter={menuOption}
              display="block"
            />
          )}
          {menuOption.isArchived && <ArchivedBadge alignSelf="flex-end" />}
        </Flex>
        {(menuOption.code || menuOption.address?.formatted) && (
          <Box fontSize=".75em" opacity={0.6} isTruncated>
            {menuOption.code && (
              <Box as="span" fontFamily="mono" fontWeight={600}>
                {menuOption.code}
              </Box>
            )}
            {menuOption.code && menuOption.address?.formatted && (
              <Box as="span" display="inline-block" fontWeight={500} mx=".5em">
                |
              </Box>
            )}
            {menuOption.address?.formatted}
          </Box>
        )}
      </components.Option>
    );
  }
  return DistributionCenterOption;
}

const DistributionCenterOption = makeDistributionCenterOption();
const DistributionCenterOptionNoRetailer = makeDistributionCenterOption(true);

export type DistributionCenterSelectProps<IsMulti extends boolean = false> =
  Omit<AsyncProps<Option, IsMulti, GroupBase<Option>>, "onChange" | "value"> &
    IdValueSelectProps<IsMulti> & {
      valueQueryOptions?: Omit<
        QueryHookOptions<
          DocumentType<typeof valueQuery>,
          DocumentVariablesType<typeof valueQuery>
        >,
        "variables"
      >;
    } & {
      filter?: DistributionCenterFilter;
      isArchivedIncluded?: boolean;
      isAddedToBrand?: boolean;
      isAdmin?: boolean;
      isOrderingPlatformAddedToBrand?: boolean;
      orderingPlatformId?: UUID | null;
      themingProps?: SelectThemingProps;
    };

export function useDistributionCenterSelect<IsMulti extends boolean = false>({
  components: componentsProp,
  isArchivedIncluded = false,
  isAddedToBrand,
  isAdmin,
  isMulti,
  isOrderingPlatformAddedToBrand,
  filter: filterProp,
  onChange,
  orderingPlatformId,
  styles,
  themingProps,
  value: idValue,
  valueQueryOptions,
  ...rest
}: DistributionCenterSelectProps<IsMulti>) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);
  const client = useApolloClient();

  const getOptionLabel = useCallback(
    (option: Option) => {
      if (isNewOption(option)) return option.label;
      const valueOption = getFragmentData(valueOptionFragment, option);
      return orderingPlatformId
        ? valueOption.name
        : valueOption.fullDisplayName;
    },
    [orderingPlatformId],
  );

  const brandId = useCurrentBrandId();
  const context = useMemo(() => {
    if (isAdmin) return ADMIN_CONTEXT;
    if (brandId) return getActiveBrandIdContext(brandId);
  }, [isAdmin, brandId]);

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.distributionCentersList),
    isMulti,
    getOptionLabel,
    onChange,
    query: valueQuery,
    queryOptions: { ...valueQueryOptions, context },
    value: idValue,
  });

  const latestFilterPropRef = useLatestRef(filterProp);

  const minInputLength = orderingPlatformId ? 1 : 2;

  const noOptionsMessage = useCallback(
    ({ inputValue }: { inputValue: string }) => {
      if (inputValue?.length >= minInputLength)
        return <>No distribution centers found containing: “{inputValue}”</>;
      return `Type at least ${minInputLength} character${
        minInputLength === 1 ? "" : "s"
      } to search…`;
    },
    [minInputLength],
  );

  const asyncFetchProps = useAsyncFetchOptions({
    cacheUsing: filterProp,
    minInputLength,
    fetchOptions: useCallback(
      (inputValue) => {
        const trimmedInputValue = inputValue.trim();

        const filters: DistributionCenterFilter[] = [];

        const isIdSearch = /^[0-9a-f-]{32,36}$/i.test(trimmedInputValue);

        if (isIdSearch) {
          filters.push({ id: { equalTo: trimmedInputValue } });
        } else {
          const searchFilters: DistributionCenterFilter[] = [
            {
              [orderingPlatformId ? "name" : "fullDisplayName"]: {
                includesInsensitive: inputValue,
              },
            },
            { code: { includesInsensitive: inputValue } },
            {
              address: { formatted: { includesInsensitive: inputValue } },
            },
          ];

          filters.push({ or: searchFilters });
        }

        if (isAdmin) {
          filters.push({ orderingPlatform: { forBrandId: { isNull: true } } });
        }

        if (orderingPlatformId) {
          filters.push({ orderingPlatformId: { equalTo: orderingPlatformId } });
        }

        if (latestFilterPropRef.current) {
          filters.push(latestFilterPropRef.current);
        }

        const orderingPlatformAddedToBrandFilter = {
          orderingPlatform: {
            brandOrderingPlatforms: {
              some: { brandId: { equalTo: brandId } },
            },
          },
        };

        const archivedFilter = isArchivedIncluded
          ? {}
          : { isArchived: { equalTo: false } };

        if (!isArchivedIncluded) {
          filters.push(archivedFilter);
        }

        if (isAddedToBrand) {
          filters.push({
            or: [
              {
                brandDistributionCenters: {
                  some: { brandId: { equalTo: brandId }, ...archivedFilter },
                },
              },
              {
                name: { isNull: true },
                ...archivedFilter,
                ...orderingPlatformAddedToBrandFilter,
              },
            ],
          });
        }

        if (isOrderingPlatformAddedToBrand) {
          filters.push(orderingPlatformAddedToBrandFilter);
        }

        const filter: DistributionCenterFilter = { and: filters };

        return client
          .query({
            query,
            variables: {
              filter,
              isOrderingPlatformHidden: !!orderingPlatformId,
            },
            fetchPolicy: "network-only",
            context,
          })
          .then(({ data }) => {
            assertPresence(
              data.distributionCenters,
              "Could not get distribution centers from response.",
            );
            const { nodes, totalCount } = data.distributionCenters;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [
        isArchivedIncluded,
        isAddedToBrand,
        isAdmin,
        isOrderingPlatformAddedToBrand,
        brandId,
        context,
        client,
        latestFilterPropRef,
        orderingPlatformId,
      ],
    ),
  });

  return {
    ...styleProps,
    ...idValueProps,
    ...asyncFetchProps,
    isMulti,
    noOptionsMessage,
    placeholder: `Select distribution center${isMulti ? "s" : ""}…`,
    components: {
      ...styleProps.components,
      ...asyncFetchProps.components,
      Option: orderingPlatformId
        ? DistributionCenterOptionNoRetailer
        : DistributionCenterOption,
      ...componentsProp,
    },
    ...rest,
  };
}

export type DistributionCenterSelectInstance<IsMulti extends boolean = false> =
  SelectInstance<Option, IsMulti>;

function DistributionCenterSelectInner<IsMulti extends boolean = false>(
  props: DistributionCenterSelectProps<IsMulti>,
  ref: ForwardedRef<DistributionCenterSelectInstance<IsMulti>>,
) {
  const selectProps = useDistributionCenterSelect(props);

  return <AsyncSelect<Option, IsMulti> ref={ref} {...selectProps} />;
}

export default forwardRef(DistributionCenterSelectInner);
